import defaultDomain from "../../../Assets/images/defaultDomain.svg";
import themeSubscriptionIcon from "../../../Assets/images/themeSubscriptionIcon.svg";
import brandWebsiteSubscriptionIcon from "../../../Assets/images/brandWebsiteSubscriptionIcon.svg";
import ecommSubscriptionIcon from "../../../Assets/images/ecommSubscriptionIcon.svg";
import googleWorkSpaceIcon from "../../../Assets/images/googleWorkSpaceIcon.svg";
import wabaSubscriptionIcon from "../../../Assets/images/wabaSubscriptionIcon.svg";
import gstSubscriptionIcon from "../../../Assets/images/gstSubscriptionIcon.svg";
import domainRedirectionIcon from "../../../Assets/images/redirectionIcon.svg";
import loaderWheel from "../../../Assets/images/loader-wheel.gif";
import store from "../../../Redux/Store/index";
import { getDaysDiffFromToday, getHybridRoute, isDesktop } from "../../../Utils/common";
import getEcommTextOrIcon from "@/Utils/getEcommTextOrIcon";
import {
  ACTIVE_SUBSCRIPTION_CTA_TEXT,
  CALLBACK_INFO,
  LIFETIME_ACCESS_DATE,
  PREMIUM_PAGE_ROUTES,
  SUBSCRIPTIONS,
  SUBSCRIPTION_STATUS_TEXT,
} from "./constants";
import {
  domainRenewalDetails,
  getWorkspaceRenewalDetails,
  requestCallback,
  toggleDomainRenewalModal,
  toggleRequestCallbackModal,
  toggleWorkspaceRenewalModal,
} from "../../../Redux/Actions";
import { ToastNotifyError } from "../../../Components/Toast";
import { getDigitalDukaanWebviewLink, getRoutingLink } from "./getRoutingLink";
import { supportRequestMailToExpertDotpe } from "../../../Utils/mailerHelper";
import { sendMessageWebview } from "../../../Utils/webview";
import { DOMAIN_DEACTIVATED_OUT_OF_GRACE_PERIOD_STATUS } from "../../../Utils/constants";
import { TitanEmailIcon } from "@/Utils/SVGExports/TitanWorkspace/TitanEmailIcon";
import { BRAND_WEBSITE_RENEWAL_PAGE, SUBSCRIPTION_DETAILS, TITAN_DASHBOARD } from "../../../Constants/routesConstant";
import { WORKSPACE_INFO_STATUS } from "../../../Constants/workspace";
import { SUBSCRIPTION_TYPE } from "@/Constants/index";
import { DOMAIN_STATUS } from "@/Constants/domain";
import { goToRoute } from "@/Utils/goToRoute";
import { shouldDisableRenewals } from "@/Utils/subscription";

const isDashy = !location.pathname.includes("webview");
const dispatch = store.dispatch;

const INVALID_DATE_STRING = "Invalid Date";
const ACTIVATION_IN_PROGRESS = "Activation in progress";
const TITAN_ERROR_MESSAGE = "We could not setup your email";

function getSubscriptionStatusText(expiryDate: string, aboutToExpireRange = 30) {
  const { LIFETIME_ACCESS, ACTIVE, EXPIRED, ABOUT_TO_EXPIRE } = SUBSCRIPTION_STATUS_TEXT;

  if (expiryDate === LIFETIME_ACCESS_DATE) {
    return LIFETIME_ACCESS;
  }

  const daysDifference = getDaysDiffFromToday(expiryDate);
  const isAboutToExpire = daysDifference <= aboutToExpireRange && daysDifference >= 0;
  const isExpired = daysDifference < 0;

  if (isAboutToExpire) {
    return ABOUT_TO_EXPIRE;
  } else {
    return isExpired ? EXPIRED : ACTIVE;
  }
}

function getWorkspaceSubMessage(status) {
  if (status === WORKSPACE_INFO_STATUS.WORKSPACE_STATUS_PAYMENT_RECEIVED) {
    return "You can access your email accounts once we finish setting up your domain.";
  }
  return "";
}

function getStatusTextStyleConfig(statusText: string) {
  const { LIFETIME_ACCESS, ABOUT_TO_EXPIRE, EXPIRED, BLOCKED } = SUBSCRIPTION_STATUS_TEXT;
  switch (statusText) {
    case LIFETIME_ACCESS:
      return {
        color: "#6B6B6B",
        bgColor: "none",
      };
    case ABOUT_TO_EXPIRE:
      return {
        color: "#E89C00",
        bgColor: "#FCF3DE",
      };
    case EXPIRED:
    case BLOCKED:
      return {
        color: "#D91100",
        bgColor: "#FBE7E6",
      };
    default:
      return {
        color: "none",
        bgColor: "none",
      };
  }
}

function formatDate(dateStr: string | undefined) {
  /* in case of workspace during activation
  we would be receiving and empty string "" in expiry date,
  and in that case we would be returning INVALID_DATE_STRING
  */
  if (dateStr === "") {
    return INVALID_DATE_STRING;
  }

  if (dateStr === LIFETIME_ACCESS_DATE) {
    return "";
  }
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateStr);

  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear().toString().substr(-2);
  return `${day} ${month}'${year}`;
}

export function getDomainName() {
  const subscriptionDetails = store.getState().themeReducer.subscriptionDetails;
  return subscriptionDetails?.domain?.domain_name || subscriptionDetails?.externalDomain?.domain_name;
}

export function renewDomainSubscription() {
  if (isDashy) {
    const payload = {
      domain: getDomainName(),
      year: 1,
    };
    const callback = (status: boolean) => {
      if (status) {
        dispatch(toggleDomainRenewalModal(true));
      } else {
        ToastNotifyError("Cannot renew domain currently. Please try again later!");
      }
    };
    dispatch(domainRenewalDetails(payload, callback));
  } else {
    getDigitalDukaanWebviewLink(
      PREMIUM_PAGE_ROUTES.RENEW_DOMAIN_SUBSCRIPTION.digitalDukaanRoute,
      `&domain-name=${getDomainName()}`,
    );
  }
}

function viewDomainDetails() {
  getRoutingLink(PREMIUM_PAGE_ROUTES.VIEW_DOMAIN_DETAILS);
}

function editEcommTheme(callback: any) {
  callback && callback();
}

function gotoSubscriptionDetails() {
  goToRoute(getHybridRoute(SUBSCRIPTION_DETAILS));
}

function renewBrandWebsiteSubscription() {
  goToRoute(getHybridRoute(BRAND_WEBSITE_RENEWAL_PAGE));
}

function exploreBrandWebsiteThemes() {
  getRoutingLink(PREMIUM_PAGE_ROUTES.EXPLORE_BRAND_WEBSITE);
}

function renewEcommPack() {
  getRoutingLink(PREMIUM_PAGE_ROUTES.ECOMM_PACK_RENEW);
}

function viewEcommPackBenefits() {
  if (isDashy) {
    getRoutingLink(PREMIUM_PAGE_ROUTES.ECOMM_PACK_SUBSCRIPTION_DETAILS);
  }
}

function renewWorkspaceSubscription({ domain }) {
  if (isDashy) {
    const renewalBtnCallback = (status: boolean) => {
      if (status) {
        dispatch(toggleWorkspaceRenewalModal(true, domain));
      } else {
        ToastNotifyError("Unable to fetch workspace renewal details!");
      }
    };
    dispatch(getWorkspaceRenewalDetails(domain, renewalBtnCallback));
  } else {
    const routeObj = PREMIUM_PAGE_ROUTES.RENEW_WORKSPACE_SUBSCRIPTION;
    getRoutingLink({
      ...routeObj,
      digitalDukaanRoute: routeObj.digitalDukaanRoute + `?domain=${domain}`,
    });
  }
}

function viewWorkspaceDetails(domain) {
  const routeObj = PREMIUM_PAGE_ROUTES.VIEW_WORKSPACE_DETAILS;
  getRoutingLink({
    ...routeObj,
    url: routeObj.url + `?domain=${domain}`,
    digitalDukaanRoute: routeObj.digitalDukaanRoute + `?domain=${domain}`,
  });
}

function viewWabaSubscriptionDetails() {
  getRoutingLink(PREMIUM_PAGE_ROUTES.WHATSAPP_MARKETING);
}

function renewWabaSubscription() {
  getRoutingLink(PREMIUM_PAGE_ROUTES.WHATSAPP_MARKETING);
}

function renewGstSubscription() {
  dispatch(
    requestCallback(CALLBACK_INFO.GST, () => {
      dispatch(toggleRequestCallbackModal(true));
    }),
  );
}

function goToTitanWorkspaceDetails(path) {
  getRoutingLink({ url: TITAN_DASHBOARD, additionalParamString: `&domain=${path}` });
}

// function to define CTA text and it's handler for a subscription
function getCtaTextAndHandler(subscriptionName: string, subscriptionStatusText: string, subscriptionData?: any) {
  const upcomingEcomSubscription = store.getState().themeReducer.subscriptionDetails.upcomingEcomSubscription;
  const { DOMAIN, THEME, BRAND_WEBSITE, WORKSPACE, ECOMM, WABA, GST, EXTERNAL_DOMAIN, TITAN } =
    ACTIVE_SUBSCRIPTION_CTA_TEXT;
  const { ABOUT_TO_EXPIRE, EXPIRED, BLOCKED } = SUBSCRIPTION_STATUS_TEXT;
  const isAboutToExpire = subscriptionStatusText === ABOUT_TO_EXPIRE;
  const isExpired = subscriptionStatusText === EXPIRED;
  const isBlocked = subscriptionStatusText === BLOCKED;
  const isExpiredOrAboutToExpire = isExpired || isAboutToExpire;
  const isOutOfGracePeriod =
    store.getState().themeReducer?.subscriptionDetails?.domain?.status ===
    DOMAIN_DEACTIVATED_OUT_OF_GRACE_PERIOD_STATUS;

  switch (subscriptionName) {
    case SUBSCRIPTIONS.DOMAIN: {
      const viewDetailsCtaInfo = {
        text: DOMAIN.VIEW_DETAILS,
        handler: viewDomainDetails,
      };
      const renewCtaInfo = {
        text: DOMAIN.RENEW,
        handler: renewDomainSubscription,
      };

      return isExpiredOrAboutToExpire && !isOutOfGracePeriod && !isBlocked
        ? [viewDetailsCtaInfo, renewCtaInfo]
        : viewDetailsCtaInfo;
    }
    case SUBSCRIPTIONS.EXTERNAL_DOMAIN:
      return {
        text: EXTERNAL_DOMAIN.VIEW_DETAILS,
        handler: viewDomainDetails,
      };
    case SUBSCRIPTIONS.ECOMM_THEME:
      return {
        text: THEME.EDIT,
        handler: editEcommTheme,
      };
    case SUBSCRIPTIONS.BRAND_WEBSITE_THEME:
      if (isExpired) {
        return [
          {
            text: BRAND_WEBSITE.RENEW,
            handler: shouldDisableRenewals() ? gotoSubscriptionDetails : renewBrandWebsiteSubscription,
          },
        ];
      } else if (isAboutToExpire) {
        return [
          {
            text: BRAND_WEBSITE.CHANGE_THEME,
            handler: exploreBrandWebsiteThemes,
          },
          {
            text: BRAND_WEBSITE.RENEW,
            handler: shouldDisableRenewals() ? gotoSubscriptionDetails : renewBrandWebsiteSubscription,
          },
        ];
      } else {
        return {
          text: BRAND_WEBSITE.CHANGE_THEME,
          handler: exploreBrandWebsiteThemes,
        };
      }
    case SUBSCRIPTIONS.GOOGLE_WORKSPACE: {
      const viewDetailsCtaInfo = {
        text: WORKSPACE.VIEW_DETAILS,
        handler: () => viewWorkspaceDetails(subscriptionData.domain),
      };
      const aboutToExpireCtaInfo = {
        text: WORKSPACE.RENEW,
        handler: () => renewWorkspaceSubscription({ domain: subscriptionData.domain }),
      };
      const expireCtaInfo = {
        text: WORKSPACE.CONTACT_US,
        handler: supportRequestMailToExpertDotpe,
      };

      return isExpired
        ? [expireCtaInfo, viewDetailsCtaInfo]
        : isAboutToExpire
          ? [aboutToExpireCtaInfo, viewDetailsCtaInfo]
          : viewDetailsCtaInfo;
    }
    case SUBSCRIPTIONS.ECOMM_PACK:
      if (!upcomingEcomSubscription && isExpiredOrAboutToExpire) {
        return {
          text: ECOMM.RENEW,
          handler: renewEcommPack,
        };
      } else {
        return {
          text: isDashy ? ECOMM.VIEW_BENFITS : ECOMM.NO_TEXT,
          handler: viewEcommPackBenefits,
        };
      }
    case SUBSCRIPTIONS.WABA:
      if (isExpiredOrAboutToExpire) {
        return {
          text: WABA.RENEW,
          handler: renewWabaSubscription,
        };
      } else {
        return {
          text: WABA.VIEW_DETAILS,
          handler: viewWabaSubscriptionDetails,
        };
      }
    case SUBSCRIPTIONS.GST:
      if (isExpiredOrAboutToExpire) {
        return {
          text: GST.RENEW,
          handler: renewGstSubscription,
        };
      } else {
        return {
          text: GST.NO_TEXT,
          handler: null,
        };
      }
    case SUBSCRIPTIONS.TITAN_WORKSPACE:
      return {
        text: TITAN.VIEW_DETAILS,
        handler: () => goToTitanWorkspaceDetails(subscriptionData.domain),
        disabled: subscriptionData?.status === WORKSPACE_INFO_STATUS.WORKSPACE_STATUS_PAYMENT_RECEIVED,
      };
  }
}

function domainRedirectionHandler(domainName: string) {
  if (isDashy) {
    window.open(`https://${domainName}`, "_blank");
  } else {
    sendMessageWebview({
      redirectBrowser: true,
      data: `https://${domainName}`,
      redirectURL: `https://${domainName}`,
    });
  }
}

function getExpiryOrValidityMessage(
  formatedExpiryDate: string,
  subscriptionStatusText: string,
  customInvalidDenoter = null,
) {
  // in case of workspace activation in progress
  if (formatedExpiryDate === INVALID_DATE_STRING) {
    return customInvalidDenoter || ACTIVATION_IN_PROGRESS;
  }
  const { EXPIRED } = SUBSCRIPTION_STATUS_TEXT;
  return (
    formatedExpiryDate &&
    (subscriptionStatusText === EXPIRED ? `Expired On ${formatedExpiryDate}` : `Valid Till ${formatedExpiryDate}`)
  );
}

/* In future, if any new subscription is added,
 add it's function here to get the required information of this subscription on the active subscripton page.
 and call this function on active subscription page, inside 'userSubscriptions' array.
 */
export function getDomainSubscriptionData() {
  const storeData = store.getState().storeReducer.store;
  const logo = storeData?.store_info?.logo_image;
  const subscriptionDetails = store.getState()?.themeReducer?.subscriptionDetails;

  const hasInternalDomain = subscriptionDetails?.domain;

  const domainSubscriptionDetails = hasInternalDomain
    ? subscriptionDetails?.domain
    : subscriptionDetails?.externalDomain;
  const subscriptionName = hasInternalDomain ? SUBSCRIPTIONS.DOMAIN : SUBSCRIPTIONS.EXTERNAL_DOMAIN;
  const expiryDate = domainSubscriptionDetails?.expiry;

  const subscriptionStatusText =
    domainSubscriptionDetails?.status === DOMAIN_STATUS.DOMAIN_BLOCKED
      ? SUBSCRIPTION_STATUS_TEXT.BLOCKED
      : getSubscriptionStatusText(expiryDate);

  const formatedExpiryDate = formatDate(expiryDate);

  return {
    logo: logo ? logo : defaultDomain,
    title: "Your Active Domain",
    subTitle: domainSubscriptionDetails?.domain_name,
    iconWithSubtitle: domainRedirectionIcon,
    iconWithSubtitleHandler: () => domainRedirectionHandler(domainSubscriptionDetails?.domain_name),
    subscriptionStatusText: subscriptionStatusText,
    statusTextStyleConfig: getStatusTextStyleConfig(subscriptionStatusText),
    ctaInfo: getCtaTextAndHandler(subscriptionName, subscriptionStatusText),
    expiryOrValidityMessage: getExpiryOrValidityMessage(formatedExpiryDate, subscriptionStatusText),
    subscriptionName: subscriptionName,
    registeredDomain: domainSubscriptionDetails?.domain_name,
  };
}

function getThemeType(subscriptionType: string) {
  switch (subscriptionType) {
    case SUBSCRIPTION_TYPE.CUSTOM_THEME:
      return "Custom Theme";
    case SUBSCRIPTION_TYPE.THEME_PRO:
      return "Theme Plus";
    default:
      return "Premium Theme";
  }
}

export function getEcommThemeSubscriptionData() {
  const subscriptionDetails = store.getState().themeReducer.subscriptionDetails;
  const ecommThemeSubscriptionDetails = subscriptionDetails?.theme;
  const isCustomTheme = ecommThemeSubscriptionDetails?.subscription_type === SUBSCRIPTION_TYPE.CUSTOM_THEME;
  const subscriptionName = SUBSCRIPTIONS.ECOMM_THEME;
  const expiryDate = ecommThemeSubscriptionDetails?.expiry;
  const subscriptionStatusText = getSubscriptionStatusText(expiryDate);
  const formatedExpiryDate = formatDate(expiryDate);
  const subtitle = getThemeType(ecommThemeSubscriptionDetails?.subscription_type);

  return {
    logo: themeSubscriptionIcon,
    title: "",
    subTitle: subtitle,
    iconWithSubtitle: null,
    iconWithSubtitleHandler: null,
    subscriptionStatusText: subscriptionStatusText,
    statusTextStyleConfig: getStatusTextStyleConfig(subscriptionStatusText),
    ctaInfo: isCustomTheme && !isDesktop() ? [] : getCtaTextAndHandler(subscriptionName, subscriptionStatusText),
    expiryOrValidityMessage: getExpiryOrValidityMessage(formatedExpiryDate, subscriptionStatusText),
    subscriptionName: subscriptionName,
    isCustomTheme: isCustomTheme,
  };
}

export function getBrandWebsiteSubscriptionData() {
  const subscriptionDetails = store.getState().themeReducer.subscriptionDetails;
  const brandWebsiteSubscriptionDetails = subscriptionDetails?.brandWebsiteSubscription;

  const subscriptionName = SUBSCRIPTIONS.BRAND_WEBSITE_THEME;
  const expiryDate = brandWebsiteSubscriptionDetails?.expiry;
  const subscriptionStatusText = getSubscriptionStatusText(expiryDate);
  const formatedExpiryDate = formatDate(expiryDate);
  const subscription_type = brandWebsiteSubscriptionDetails?.subscription_type === "brand_pro" ? "Pro" : "Plus";

  return {
    logo: brandWebsiteSubscriptionIcon,
    title: "",
    subTitle: `Premium Website ${subscription_type}`,
    iconWithSubtitle: null,
    iconWithSubtitleHandler: null,
    subscriptionStatusText: subscriptionStatusText,
    statusTextStyleConfig: getStatusTextStyleConfig(subscriptionStatusText),
    ctaInfo: getCtaTextAndHandler(subscriptionName, subscriptionStatusText),
    expiryOrValidityMessage: getExpiryOrValidityMessage(formatedExpiryDate, subscriptionStatusText),
    subscriptionName: subscriptionName,
  };
}

export function getEcommPackSubscriptionData() {
  const subscriptionDetails = store.getState().themeReducer.subscriptionDetails;
  const ecommPackSubscriptionDetails = subscriptionDetails.ecomSubscription;

  const subscriptionName = SUBSCRIPTIONS.ECOMM_PACK;
  const expiryDate = ecommPackSubscriptionDetails?.expiry;
  const subscriptionStatusText = getSubscriptionStatusText(expiryDate);
  const formatedExpiryDate = formatDate(expiryDate);

  const ecommTextOrIcon = getEcommTextOrIcon(ecommPackSubscriptionDetails?.subscription_type);

  return {
    logo: ecommSubscriptionIcon,
    title: "",
    subTitle: `E-Commerce ${ecommTextOrIcon?.text}`,
    iconWithSubtitle: ecommTextOrIcon?.icon,
    iconWithSubtitleHandler: null,
    subscriptionStatusText: subscriptionStatusText,
    statusTextStyleConfig: getStatusTextStyleConfig(subscriptionStatusText),
    ctaInfo: getCtaTextAndHandler(subscriptionName, subscriptionStatusText),
    expiryOrValidityMessage: getExpiryOrValidityMessage(formatedExpiryDate, subscriptionStatusText),
    subscriptionName: subscriptionName,
  };
}

export function getUpcomingEcommSubscriptionData() {
  const subscriptionDetails = store.getState().themeReducer.subscriptionDetails;
  const upcomingEcomSubscription = subscriptionDetails.upcomingEcomSubscription;

  const subscriptionName = SUBSCRIPTIONS.ECOMM_PACK;
  const activationDate = upcomingEcomSubscription?.created_at;
  const formatedExpiryDate = formatDate(activationDate);

  const ecommTextOrIcon = getEcommTextOrIcon(upcomingEcomSubscription?.subscription_type);
  return {
    logo: ecommSubscriptionIcon,
    title: "",
    subTitle: `E-Commerce ${ecommTextOrIcon?.text}`,
    iconWithSubtitle: ecommTextOrIcon?.icon,
    iconWithSubtitleHandler: null,
    subscriptionStatusText: "Upcoming",
    statusTextStyleConfig: {
      color: "#222222",
      bgColor: "#E9E9E9",
    },
    ctaInfo: [],
    expiryOrValidityMessage: `Activates on ${formatedExpiryDate}`,
    subscriptionName: subscriptionName,
  };
}

export function getWabaSubscriptionData() {
  const subscriptionDetails = store.getState().themeReducer?.subscriptionDetails;
  const wabaSubscriptionDetails = subscriptionDetails?.waba;

  const subscriptionName = SUBSCRIPTIONS.WABA;
  const expiryDate = wabaSubscriptionDetails?.expiry;
  const subscriptionStatusText = getSubscriptionStatusText(expiryDate);
  const formatedExpiryDate = formatDate(expiryDate);

  return {
    logo: wabaSubscriptionIcon,
    title: "",
    subTitle: "WhatsApp Business",
    iconWithSubtitle: null,
    iconWithSubtitleHandler: null,
    subscriptionStatusText: subscriptionStatusText,
    statusTextStyleConfig: getStatusTextStyleConfig(subscriptionStatusText),
    ctaInfo: getCtaTextAndHandler(subscriptionName, subscriptionStatusText),
    expiryOrValidityMessage: getExpiryOrValidityMessage(formatedExpiryDate, subscriptionStatusText),
    subscriptionName: subscriptionName,
  };
}

export function getGstSubscriptionData() {
  const subscriptionDetails = store.getState().themeReducer.subscriptionDetails;
  const gstSubscriptionDetails = subscriptionDetails?.gstSubscription;

  const subscriptionName = SUBSCRIPTIONS.GST;
  const expiryDate = gstSubscriptionDetails?.expiry;
  const subscriptionStatusText = getSubscriptionStatusText(expiryDate);
  const formatedExpiryDate = formatDate(expiryDate);

  return {
    logo: gstSubscriptionIcon,
    title: "",
    subTitle: "GST Pack",
    iconWithSubtitle: null,
    iconWithSubtitleHandler: null,
    subscriptionStatusText: subscriptionStatusText,
    statusTextStyleConfig: getStatusTextStyleConfig(subscriptionStatusText),
    ctaInfo: getCtaTextAndHandler(subscriptionName, subscriptionStatusText),
    expiryOrValidityMessage: getExpiryOrValidityMessage(formatedExpiryDate, subscriptionStatusText),
    subscriptionName: subscriptionName,
  };
}

export function getWorkspaceSubscriptionData() {
  const subscriptionDetails = store.getState().themeReducer.subscriptionDetails;
  const subscriptionName = SUBSCRIPTIONS.GOOGLE_WORKSPACE;

  const googleWorkspaceMap = {};
  Object.keys(subscriptionDetails).forEach((item: any) => {
    if (subscriptionDetails[item]?.subscription_type === subscriptionName) {
      const domainName = item.split("google_workspace_")?.[1];
      googleWorkspaceMap[item] = { ...subscriptionDetails[item], domain: domainName };
    }
  });
  const googleWorkspaceList = Object.values(googleWorkspaceMap);

  const renderData = googleWorkspaceList.map((item: any) => {
    const expiryDate = item?.expiry;
    const subscriptionStatusText = getSubscriptionStatusText(expiryDate, 30);
    const formatedExpiryDate = formatDate(expiryDate);
    const emailCount = item?.email_count || 1;

    return {
      logo: googleWorkSpaceIcon,
      title: item.domain,
      subTitle: emailCount ? `${emailCount} Active Accounts` : "",
      iconWithSubtitle: null,
      iconWithSubtitleHandler: null,
      subscriptionStatusText: subscriptionStatusText,
      statusTextStyleConfig: getStatusTextStyleConfig(subscriptionStatusText),
      ctaInfo: getCtaTextAndHandler(subscriptionName, subscriptionStatusText, item),
      expiryOrValidityMessage: getExpiryOrValidityMessage(formatedExpiryDate, subscriptionStatusText),
      subscriptionName: subscriptionName,
      registeredDomain: item.domain,
    };
  });

  return renderData;
}

export function getTitanSubscriptionData() {
  const subscriptionDetails = store?.getState()?.themeReducer?.subscriptionDetails;
  const titanUpgradeDaysBeforeExpiry =
    store?.getState().commonReducer.appConstants?.workspace?.titan_upgrade_days_before_expiry;
  const subscriptionName = SUBSCRIPTIONS.TITAN_WORKSPACE;

  const titanWorkspaceList = Object.keys(subscriptionDetails)?.reduce((acc, item: any) => {
    if (subscriptionDetails[item]?.subscription_type === subscriptionName) {
      const domainName = item.split("titan_workspace_")?.[1];
      acc[item] = { ...subscriptionDetails[item], domain: domainName };
    }
    return acc;
  }, {});

  const titanList = Object.values(titanWorkspaceList);

  const renderData = titanList.map((sub: any) => {
    const expiryDate = sub?.expiry;
    const formatedExpiryDate = formatDate(expiryDate);
    const validityText = getSubscriptionStatusText(expiryDate, titanUpgradeDaysBeforeExpiry);
    const emailCount = sub?.email_count || 1;
    const subscriptionStatusText2 = getWorkspaceSubMessage(sub?.status);

    return {
      logo: TitanEmailIcon,
      title: sub.domain,
      subTitle: `${emailCount} Titan Email Account${emailCount > 1 ? "s" : ""}`,
      iconWithSubtitle: null,
      iconWithSubtitleHandler: null,
      subscriptionStatusText: subscriptionStatusText2 || validityText,
      SubscriptionStatusTextIcon: subscriptionStatusText2 ? loaderWheel : "",
      SubscriptionStatusTextIconType: "gif",
      statusTextStyleConfig: getStatusTextStyleConfig(validityText),
      expiryOrValidityMessage: !subscriptionStatusText2
        ? getExpiryOrValidityMessage(formatedExpiryDate, validityText, TITAN_ERROR_MESSAGE)
        : "",
      ctaInfo: !subscriptionStatusText2 && getCtaTextAndHandler(subscriptionName, validityText, sub),
      subscriptionName: subscriptionName,
      uiType: subscriptionStatusText2 ? 2 : 1,
      registeredDomain: sub.domain,
    };
  });

  return renderData;
}
